import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { Card, CardContent } from "../components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import Navigation from "../components/layout/Navigation";
import Footer from "../components/layout/Footer";
import ScarcityBadge from "../components/ui/ScarcityBadge";
import DynamicPriceDisplay from "../components/ui/DynamicPriceDisplay";
import WishlistButton from "../components/ui/WishlistButton";
import ProductReviews, { StarRating } from "../components/ui/ProductReviews";
import ARViewOnWall from "../components/ui/ARViewOnWall";
import { FramedImage, useFrameConfig } from "../components/ui/FramedImage";
import CustomRequestModal from "../components/ui/CustomRequestModal";
import QuoteBuilder from "../components/ui/QuoteBuilder";
import StockUrgency from "../components/ui/StockUrgency";
import RecentlyViewed, { addToRecentlyViewed } from "../components/ui/RecentlyViewed";
import SocialShareButtons from "../components/ui/SocialShareButtons";
import Breadcrumbs from "../components/ui/Breadcrumbs";
import { ProductSchema } from "../components/seo/StructuredData";
import { toast } from "sonner";
import { 
  ShoppingCart, Shield, Truck, Award, ArrowLeft, Check, Building, 
  TrendingUp, AlertCircle, ZoomIn, ChevronLeft, ChevronRight, 
  Ruler, Package, FileText, X, Heart, Flame, Clock, Camera, Frame, Tag, Palette
} from "lucide-react";
import { trackAddToCart, trackProductView } from "../utils/analytics";
import { API } from "../apiConfig";


// Size comparison visual data
const SIZE_DIMENSIONS = {
  "A4": { width: 21, height: 29.7, label: "A4 (21×30cm)" },
  "A3": { width: 29.7, height: 42, label: "A3 (30×42cm)" },
  "A2": { width: 42, height: 59.4, label: "A2 (42×59cm)" },
  "A1": { width: 59.4, height: 84.1, label: "A1 (59×84cm)" },
  "Custom": { width: 100, height: 70, label: "Mural (Custom)" }
};

const ProductDetailPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [showSizeGuide, setShowSizeGuide] = useState(false);
  const [showARView, setShowARView] = useState(false);
  const [showCustomRequest, setShowCustomRequest] = useState(false);
  const [interestedInFraming, setInterestedInFraming] = useState(false);

  const isWholesale = user?.role === "wholesale" && user?.wholesale_status === "approved";

  useEffect(() => {
    // Scroll to top and reset state when product changes
    window.scrollTo(0, 0);
    setSelectedImage(0);
    setSelectedVariant(null);
    setQuantity(1);
    setLoading(true);
    fetchProduct();
    fetchRelatedProducts();
  }, [productId]);

  // Track product view and add to recently viewed when product loads
  useEffect(() => {
    if (product) {
      addToRecentlyViewed(product);
      trackProductView(product.product_id, product.title);
    }
  }, [product?.product_id]);

  const fetchProduct = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/products/${productId}`, { headers });
      setProduct(response.data);
      
      // Auto-select first variant if product has variants
      if (response.data.has_variants && response.data.variants?.length > 0) {
        const firstVariant = response.data.variants[0];
        setSelectedVariant(firstVariant);
        if (isWholesale && firstVariant) {
          setQuantity(firstVariant.wholesale_min_qty || 1);
        }
      } else if (isWholesale && response.data.wholesale_min_qty) {
        setQuantity(response.data.wholesale_min_qty);
      }
    } catch (error) {
      console.error("Error fetching product:", error);
      toast.error("Failed to load product");
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedProducts = async () => {
    try {
      // Fetch from same category for better relevance
      const cat = product?.category || "";
      const url = cat
        ? `${API}/products?limit=8&category=${cat}`
        : `${API}/products?limit=8`;
      const response = await axios.get(url);
      const filtered = response.data
        .filter(p => p.product_id !== productId)
        .slice(0, 4);
      // If not enough from same category, backfill
      if (filtered.length < 4) {
        const backup = await axios.get(`${API}/products?limit=8`);
        const extra = backup.data
          .filter(p => p.product_id !== productId && !filtered.find(f => f.product_id === p.product_id))
          .slice(0, 4 - filtered.length);
        setRelatedProducts([...filtered, ...extra]);
      } else {
        setRelatedProducts(filtered);
      }
    } catch (error) {
      console.error("Error fetching related products:", error);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(price);
  };

  const handleVariantChange = (variant) => {
    setSelectedVariant(variant);
    if (isWholesale) {
      setQuantity(variant.wholesale_min_qty || 1);
    }
  };

  const handleAddToCart = async () => {
    if (!product) return;
    
    // Validation for variants
    if (product.has_variants && !selectedVariant) {
      toast.error("Please select a size");
      return;
    }
    
    setAdding(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Build cart item
      const cartItem = {
        product_id: product.product_id,
        variant_id: selectedVariant?.variant_id,
        quantity: isWholesale ? quantity : 1,
      };
      
      // Add framing interest flag if checked
      if (interestedInFraming) {
        cartItem.framing_requested = true;
        cartItem.notes = "Customer interested in professional framing - please follow up with quote";
      }
      
      await axios.post(`${API}/cart/add`, cartItem, { headers, withCredentials: true });
      
      // Track add to cart event
      const price = selectedVariant?.price || product.base_price || 0;
      trackAddToCart(product.product_id, product.title, price, isWholesale ? quantity : 1);
      
      const framingMsg = interestedInFraming ? " (framing quote requested)" : "";
      toast.success(`Added to cart${framingMsg}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add to cart");
    } finally {
      setAdding(false);
    }
  };

  const nextImage = () => {
    if (product?.images?.length > 1) {
      setSelectedImage((prev) => (prev + 1) % product.images.length);
    }
  };

  const prevImage = () => {
    if (product?.images?.length > 1) {
      setSelectedImage((prev) => (prev - 1 + product.images.length) % product.images.length);
    }
  };

  // Get current price and stock
  const retailPrice = selectedVariant?.price || product?.price || 0;
  const wholesalePrice = selectedVariant?.wholesale_price || product?.wholesale_price || retailPrice * 0.8;
  const currentStock = selectedVariant?.stock || product?.stock || 0;
  const minQty = selectedVariant?.wholesale_min_qty || product?.wholesale_min_qty || 1;
  
  // Edition info - Calculate total stock across all variants for progress display
  const calculateTotalStock = () => {
    if (product?.has_variants && product?.variants?.length > 0) {
      return product.variants.reduce((sum, v) => sum + (v.stock || 0), 0);
    }
    return product?.stock || 0;
  };
  
  const calculateOriginalStock = () => {
    if (product?.has_variants && product?.variants?.length > 0) {
      return product.variants.reduce((sum, v) => sum + (v.original_stock || v.stock || 0), 0);
    }
    return product?.original_stock || product?.stock || 0;
  };
  
  const totalCurrentStock = calculateTotalStock();
  const totalOriginalStock = calculateOriginalStock();
  const editionSold = totalOriginalStock > 0 ? totalOriginalStock - totalCurrentStock : 0;
  const editionTotal = totalOriginalStock;

  if (loading) {
    return (
      <div className="min-h-screen">
        <Navigation />
        <div className="pt-32 pb-24 px-6 md:px-12">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12">
              <div className="aspect-square bg-muted rounded-2xl animate-pulse" />
              <div className="space-y-6">
                <div className="h-8 bg-muted rounded w-3/4 animate-pulse" />
                <div className="h-6 bg-muted rounded w-1/4 animate-pulse" />
                <div className="h-24 bg-muted rounded animate-pulse" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen">
        <Navigation />
        <div className="pt-32 pb-24 px-6 md:px-12 text-center">
          <h1 className="text-2xl font-medium mb-4">Product not found</h1>
          <Link to="/products">
            <Button variant="outline" className="rounded-full">
              <ArrowLeft className="mr-2 h-4 w-4" /> Back to Gallery
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="product-detail-page">
      <ProductSchema product={product} />
      <Navigation />

      <section className="pt-28 pb-24 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          {/* Breadcrumbs */}
          <Breadcrumbs items={[
            { label: "The Gallery", href: "/products" },
            { label: product.category?.replace("_", " ").replace(/\b\w/g, c => c.toUpperCase()), href: `/products?category=${product.category}` },
            { label: product.title },
          ]} />

          {/* Back Button */}
          <Link to="/products" className="inline-flex items-center text-muted-foreground hover:text-foreground mb-8 transition-colors">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Collection
          </Link>

          <div className="grid lg:grid-cols-12 gap-0 lg:gap-16">
            {/* Image Gallery — Sticky left column */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-7"
            >
              <div className="lg:sticky lg:top-28">
              {/* Main Image with Lightbox */}
              <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
                <DialogTrigger asChild>
                  <div className="aspect-square rounded-2xl overflow-hidden bg-muted/30 cursor-zoom-in relative group flex items-center justify-center p-8">
                    <FramedImage
                      src={product.images?.[selectedImage] || "https://via.placeholder.com/600"}
                      alt={product.title}
                      frameScale={0.8}
                      imageScale={product.image_scale || 100}
                      className="max-w-[90%] max-h-[90%] transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors flex items-center justify-center pointer-events-none">
                      <ZoomIn className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
                    </div>
                    
                    {/* Image Navigation */}
                    {product.images?.length > 1 && (
                      <>
                        <button 
                          onClick={(e) => { e.stopPropagation(); prevImage(); }}
                          className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
                        >
                          <ChevronLeft className="h-5 w-5" />
                        </button>
                        <button 
                          onClick={(e) => { e.stopPropagation(); nextImage(); }}
                          className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
                        >
                          <ChevronRight className="h-5 w-5" />
                        </button>
                      </>
                    )}
                    
                    {/* Edition Badge */}
                    {product.is_limited_edition && (
                      <div className="absolute top-4 left-4 z-10">
                        <Badge className="bg-primary text-primary-foreground">
                          Limited Edition
                        </Badge>
                      </div>
                    )}
                  </div>
                </DialogTrigger>
                <DialogContent className="max-w-4xl p-0 bg-transparent border-0">
                  <div className="relative flex items-center justify-center p-12 bg-black/80 rounded-lg">
                    <FramedImage
                      src={product.images?.[selectedImage] || "https://via.placeholder.com/1200"}
                      alt={product.title}
                      frameScale={1.2}
                      imageScale={product.image_scale || 100}
                      className="max-w-full max-h-[75vh]"
                    />
                    <button
                      onClick={() => setLightboxOpen(false)}
                      className="absolute top-4 right-4 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Thumbnail Gallery */}
              {product.images?.length > 1 && (
                <div className="flex gap-4 mt-4">
                  {product.images.map((image, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelectedImage(idx)}
                      className={`w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                        selectedImage === idx ? 'border-primary' : 'border-transparent opacity-60 hover:opacity-100'
                      }`}
                    >
                      <img
                        src={image}
                        alt={`${product.title} view ${idx + 1}`}
                        className="w-full h-full object-contain bg-transparent"
                      />
                    </button>
                  ))}
                </div>
              )}

              {/* AR View on Wall Button */}
              <Button
                variant="outline"
                className="mt-4 w-full gap-2 border-amber-300 text-amber-700 hover:bg-amber-50"
                onClick={() => setShowARView(true)}
                data-testid="ar-view-button"
              >
                <Camera className="w-4 h-4" />
                View on Your Wall (AR)
              </Button>

              {/* Edition Counter — Enhanced Storytelling */}
              {product.is_limited_edition && editionTotal > 0 && (
                <div className="mt-6 p-5 bg-muted/50 rounded-xl border border-primary/10" data-testid="edition-counter">
                  <div className="flex items-center gap-2 mb-3">
                    <Award className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium tracking-wide">Limited Edition</span>
                  </div>
                  <div className="flex items-end justify-between mb-2">
                    <div>
                      <span className="text-3xl font-bold text-primary">{totalCurrentStock}</span>
                      <span className="text-lg text-muted-foreground"> of {editionTotal}</span>
                    </div>
                    <span className="text-xs text-muted-foreground uppercase tracking-widest">remaining</span>
                  </div>
                  <div className="h-2.5 bg-muted rounded-full overflow-hidden mb-3">
                    <div 
                      className="h-full rounded-full transition-all duration-700"
                      style={{ 
                        width: `${editionTotal > 0 ? (editionSold / editionTotal) * 100 : 0}%`,
                        background: 'linear-gradient(90deg, hsl(var(--primary)), hsl(24 80% 55%))'
                      }}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {editionSold} editions acquired by collectors worldwide. Once sold, this edition will never be reprinted.
                  </p>
                  <div className="flex gap-3 mt-3">
                    <Badge variant="outline" className="text-[10px] border-primary/30">
                      <Shield className="h-3 w-3 mr-1" /> Provenance Included
                    </Badge>
                    <Badge variant="outline" className="text-[10px] border-primary/30">
                      <Award className="h-3 w-3 mr-1" /> Certificate of Authenticity
                    </Badge>
                  </div>
                </div>
              )}
              </div>
            </motion.div>

            {/* Product Info — Scrolling right column */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="lg:col-span-5"
            >
              {/* Category & Title */}
              <div className="mb-8">
                <div className="flex items-center justify-between border-b border-foreground/15 pb-3 mb-6">
                  <p className="text-[11px] tracking-[0.3em] uppercase text-muted-foreground">
                    {product.category?.replace("_", " ")}
                  </p>
                  <p className="plate-no text-primary">The Arawol Catalogue</p>
                </div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-light leading-tight mb-4" data-testid="product-title">
                  <em>{product.title}</em>
                </h1>
                
                {/* Artist Link */}
                {product.artist && (
                  <Link 
                    to="/artist" 
                    className="text-muted-foreground hover:text-primary transition-colors text-sm tracking-wide"
                  >
                    by {product.artist.name}
                  </Link>
                )}

                {/* Stock Urgency Indicator */}
                <div className="mt-4">
                  <StockUrgency stock={currentStock} editionSize={editionTotal > 0 ? editionTotal : null} soldCount={editionSold} />
                </div>
              </div>

              {/* Description */}
              <div className="mb-8">
                <p className="text-base text-muted-foreground leading-relaxed">
                  Available as a museum-quality reproduction on your choice of substrate, size, and framing.
                </p>
              </div>

              <div className="h-px bg-border mb-8" />

              {/* Size Selection - informational for quote */}
              {product.has_variants && product.variants?.length > 0 && (
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-medium">Available Sizes</label>
                    <button 
                      onClick={() => setShowSizeGuide(true)}
                      className="text-sm text-primary hover:underline flex items-center gap-1"
                    >
                      <Ruler className="h-4 w-4" /> Size Guide
                    </button>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {product.variants.map((variant) => (
                      <div
                        key={variant.variant_id}
                        className="p-4 rounded-xl border border-border bg-muted/20"
                      >
                        <p className="font-medium">{variant.name}</p>
                        {variant.size && <p className="text-sm text-muted-foreground">{variant.size}</p>}
                        {variant.substrate && (
                          <p className="text-xs text-muted-foreground capitalize mt-0.5">
                            {variant.substrate.replace("_", " ")}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Custom sizes and substrates also available on request.
                  </p>
                </div>
              )}

              {/* Available Substrates */}
              <div className="mb-6 p-4 bg-muted/20 rounded-xl border border-border">
                <p className="font-medium text-sm mb-3 flex items-center gap-2">
                  <Palette className="h-4 w-4 text-primary" />
                  Available Substrates
                </p>
                <div className="flex flex-wrap gap-2">
                  {["Archival Paper", "Stretched Canvas", "Aluminium Dibond", "Acrylic Face Mount", "Natural Timber"].map((s) => (
                    <span key={s} className="text-xs px-3 py-1.5 rounded-full bg-background border border-border">
                      {s}
                    </span>
                  ))}
                </div>
              </div>

              {/* Request a Quote - Primary CTA */}
              <Button
                onClick={() => setShowCustomRequest(true)}
                className="w-full h-14 text-lg rounded-full bg-foreground text-background hover:bg-foreground/90 shadow-lg"
                data-testid="request-quote-btn"
              >
                <Palette className="mr-2 h-5 w-5" />
                Request a Quote
              </Button>

              <p className="text-center text-xs text-muted-foreground mt-4 tracking-wide">
                We respond within 24-48 hours with a detailed, personalised quote.
              </p>

              {/* Trust Badges */}
              <div className="grid grid-cols-3 gap-4 mt-8">
                <div className="text-center">
                  <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-muted flex items-center justify-center">
                    <Shield className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <p className="text-xs text-muted-foreground">Authenticity Guaranteed</p>
                </div>
                <div className="text-center">
                  <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-muted flex items-center justify-center">
                    <Truck className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <p className="text-xs text-muted-foreground">Secure Shipping</p>
                </div>
                <div className="text-center">
                  <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-muted flex items-center justify-center">
                    <Award className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <p className="text-xs text-muted-foreground">Certificate Included</p>
                </div>
              </div>

              {/* Social Share */}
              <div className="mt-6 pt-6 border-t border-border">
                <SocialShareButtons 
                  url={`https://arawol.art/products/${product.product_id}`}
                  title={`${product.title} - Art By Arawol`}
                  image={product.images?.[0]}
                />
              </div>
            </motion.div>
          </div>

          {/* Product Details Section */}
          <div className="mt-24 grid lg:grid-cols-3 gap-16">
            {/* Description & Story */}
            <div className="lg:col-span-2">
              <p className="text-[11px] tracking-[0.3em] uppercase text-primary mb-4">
                About This Artwork
              </p>
              <h2 className="text-3xl md:text-4xl font-light mb-8">
                <em>The Story Behind the Work</em>
              </h2>
              
              {/* Description */}
              <p className="text-muted-foreground leading-relaxed text-base mb-10">
                {product.description}
              </p>

              {/* Cultural Story — prominent editorial block */}
              {product.story && (
                <div className="mb-10 relative">
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary rounded-full" />
                  <div className="pl-8">
                    <p className="text-[11px] tracking-[0.25em] uppercase text-primary mb-3 flex items-center gap-2">
                      <Heart className="h-3.5 w-3.5" />
                      Cultural Significance
                    </p>
                    <p className="text-foreground leading-relaxed text-lg font-light italic">
                      {product.story}
                    </p>
                  </div>
                </div>
              )}

              {/* Accordion Details */}
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="details">
                  <AccordionTrigger>Product Details</AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-3 text-sm">
                      {product.medium && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Medium</span>
                          <span>{product.medium}</span>
                        </div>
                      )}
                      {product.materials && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Materials</span>
                          <span>{product.materials}</span>
                        </div>
                      )}
                      {product.dimensions && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Dimensions</span>
                          <span>{product.dimensions}</span>
                        </div>
                      )}
                      {product.year_created && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Year Created</span>
                          <span>{product.year_created}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Category</span>
                        <span className="capitalize">{product.category?.replace("_", " ")}</span>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="shipping">
                  <AccordionTrigger>Shipping & Delivery</AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-3 text-sm text-muted-foreground">
                      {product.shipping_info ? (
                        <p>{product.shipping_info}</p>
                      ) : (
                        <>
                          <p>• Ships within 5-7 business days</p>
                          <p>• Carefully packaged for safe delivery</p>
                          <p>• Tracking provided for all orders</p>
                          <p>• International shipping available</p>
                        </>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="certificate">
                  <AccordionTrigger>Certificate of Authenticity</AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-3 text-sm text-muted-foreground">
                      <p>
                        Every artwork from The White Wallaby Collection includes a 
                        Certificate of Authenticity signed by Colin Isaacs.
                      </p>
                      <div className="p-4 bg-muted/50 rounded-lg">
                        <p className="font-medium text-foreground mb-2">Certificate includes:</p>
                        <ul className="space-y-1">
                          <li>• Unique certificate number</li>
                          <li>• Artist signature</li>
                          <li>• Edition number (for limited editions)</li>
                          <li>• Artwork details and provenance</li>
                          <li>• QR code for verification</li>
                        </ul>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="returns">
                  <AccordionTrigger>Returns & Care</AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-3 text-sm text-muted-foreground">
                      <p>• 14-day return policy for undamaged items</p>
                      <p>• Prints should be kept away from direct sunlight</p>
                      <p>• Handle with clean, dry hands</p>
                      <p>• Professional framing recommended</p>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>

            {/* Artist Card */}
            <div>
              {product.artist && (
                <Card className="rounded-2xl sticky top-32">
                  <CardContent className="pt-6">
                    <div className="text-center mb-4">
                      <img
                        src={product.artist.image_url || "https://via.placeholder.com/100"}
                        alt={product.artist.name}
                        className="w-24 h-24 rounded-full mx-auto mb-4 object-cover"
                      />
                      <h3 className="font-medium text-lg">{product.artist.name}</h3>
                      <p className="text-sm text-muted-foreground">{product.artist.region}</p>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4 line-clamp-4">
                      {product.artist.biography}
                    </p>
                    <Link to="/artist">
                      <Button variant="outline" className="w-full rounded-full">
                        View Artist Profile
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>

          {/* You May Also Like */}
          {relatedProducts.length > 0 && (
            <div className="mt-32" data-testid="cross-sell-section">
              <p className="text-[11px] tracking-[0.3em] uppercase text-muted-foreground mb-3">
                From the Collection
              </p>
              <h2 className="text-3xl font-light mb-10">
                <em>You May Also Like</em>
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                {relatedProducts.map((relatedProduct) => (
                  <Link 
                    key={relatedProduct.product_id} 
                    to={`/products/${relatedProduct.product_id}`}
                    className="group"
                    data-testid={`cross-sell-${relatedProduct.product_id}`}
                  >
                    <div className="aspect-[3/4] overflow-hidden mb-4">
                      <img
                        src={relatedProduct.images?.[0] || "https://via.placeholder.com/300"}
                        alt={relatedProduct.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>
                    <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground mb-1">
                      {relatedProduct.medium || relatedProduct.category?.replace("_", " ")}
                    </p>
                    <h3 className="text-sm font-light line-clamp-1 group-hover:text-primary transition-colors">
                      <em>{relatedProduct.title}</em>
                    </h3>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Size Guide Modal */}
      <Dialog open={showSizeGuide} onOpenChange={setShowSizeGuide}>
        <DialogContent className="max-w-2xl">
          <h2 className="text-xl font-medium mb-6">Size Guide</h2>
          <div className="space-y-6">
            <p className="text-muted-foreground">
              Compare print sizes to help you choose the right artwork for your space.
            </p>
            
            {/* Visual Size Comparison */}
            <div className="relative h-64 bg-muted/30 rounded-xl p-4 flex items-end justify-center gap-4">
              {product.variants?.map((variant) => {
                const sizeData = SIZE_DIMENSIONS[variant.size] || SIZE_DIMENSIONS["A4"];
                const scale = 2; // px per cm
                return (
                  <div 
                    key={variant.variant_id}
                    className={`bg-primary/20 border-2 border-primary/40 rounded flex items-center justify-center transition-all ${
                      selectedVariant?.variant_id === variant.variant_id ? 'border-primary bg-primary/30' : ''
                    }`}
                    style={{ 
                      width: sizeData.width * scale,
                      height: sizeData.height * scale,
                      maxHeight: '200px',
                      maxWidth: '150px'
                    }}
                  >
                    <span className="text-xs font-medium">{variant.name}</span>
                  </div>
                );
              })}
            </div>

            {/* Size Table */}
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted">
                  <tr>
                    <th className="text-left p-3">Size</th>
                    <th className="text-left p-3">Dimensions</th>
                    <th className="text-left p-3">Best For</th>
                    {isWholesale && <th className="text-right p-3">Price</th>}
                  </tr>
                </thead>
                <tbody>
                  {product.variants?.map((variant) => (
                    <tr key={variant.variant_id} className="border-t">
                      <td className="p-3 font-medium">{variant.name}</td>
                      <td className="p-3 text-muted-foreground">
                        {SIZE_DIMENSIONS[variant.size]?.label || variant.size}
                      </td>
                      <td className="p-3 text-muted-foreground">
                        {variant.size === "A4" && "Desk or small wall"}
                        {variant.size === "A3" && "Medium wall space"}
                        {variant.size === "A2" && "Large wall feature"}
                        {variant.size === "A1" && "Statement piece"}
                        {variant.size === "Custom" && "Commercial/gallery"}
                      </td>
                      {isWholesale && <td className="p-3 text-right font-medium">{formatPrice(variant.price)}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Recently Viewed Section */}
      <section className="px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <RecentlyViewed excludeProductId={productId} />
        </div>
      </section>

      {/* Reviews Section */}
      <section className="py-16 px-6 md:px-12 bg-muted/30">
        <div className="max-w-4xl mx-auto">
          <ProductReviews productId={productId} />
        </div>
      </section>

      {/* AR View Modal */}
      <ARViewOnWall
        product={product}
        imageUrl={product?.images?.[selectedImage] || product?.images?.[0]}
        isOpen={showARView}
        onClose={() => setShowARView(false)}
      />

      {/* Quote Builder */}
      <QuoteBuilder
        open={showCustomRequest}
        onClose={() => setShowCustomRequest(false)}
        artworkTitle={product?.title}
        productId={product?.product_id}
        artworkImage={product?.images?.[0]}
      />

      <Footer />
    </div>
  );
};

export default ProductDetailPage;
